import React, { useState, useRef, useEffect } from 'react';
import { useAudioPlayer } from '../../context/audio-player-context';
import { Pause, Play, X, Volume1, Volume2, VolumeX, MessageSquare } from 'lucide-react';
import { Button } from '../atoms/Button';
import { motion, AnimatePresence } from 'framer-motion'; // Import framer-motion

export const GlobalAudioPlayer: React.FC = () => {
    const {
        currentMedia,
        isPlaying,
        currentTime,
        duration,
        isLoading,
        error,
        volume,
        isMuted,
        playbackRate,
        isTranscriptVisible, // Get transcript state
        play,
        pause,
        seek,
        setVolume,
        toggleMute,
        setPlaybackRate,
        unloadAudio,
        toggleTranscriptVisibility, // Get transcript action
    } = useAudioPlayer();

    // Local state for dropdowns/popups
    const [showVolumeSlider, setShowVolumeSlider] = useState(false);
    const [showRateOptions, setShowRateOptions] = useState(false);
    const volumeControlRef = useRef<HTMLDivElement>(null);
    const playbackRateRef = useRef<HTMLDivElement>(null);

    // Click outside handlers for dropdowns
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (volumeControlRef.current && !volumeControlRef.current.contains(event.target as Node)) {
                setShowVolumeSlider(false);
            }
            if (playbackRateRef.current && !playbackRateRef.current.contains(event.target as Node)) {
                setShowRateOptions(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);


    if (!currentMedia && !isLoading) {
        // Don't render anything if no media is loaded or loading
        return null;
    }

    const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!duration) return;
        const progressBar = e.currentTarget;
        const rect = progressBar.getBoundingClientRect();
        const clickPosition = e.clientX - rect.left;
        const percentage = clickPosition / rect.width;
        seek(percentage * duration);
    };

    // Format time in MM:SS format
    const formatTime = (timeInSeconds: number): string => {
        if (!isFinite(timeInSeconds) || timeInSeconds < 0) return '0:00';
        const minutes = Math.floor(timeInSeconds / 60);
        const seconds = Math.floor(timeInSeconds % 60);
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };

    const progressPercentage = duration > 0 ? (currentTime / duration) * 100 : 0;

    const playerVariants = {
        hidden: { y: "100%", opacity: 0 },
        visible: { y: 0, opacity: 1 },
        exit: { y: "100%", opacity: 0 }
    };

    return (
        <AnimatePresence>
            {currentMedia && ( // Only render the motion div if there's media
                <motion.div
                    key="global-audio-player" // Add key for AnimatePresence
                    variants={playerVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    transition={{ // Define separate transitions
                        animate: { type: "spring", damping: 25, stiffness: 180 },
                        exit: { type: "tween", duration: 0.2, ease: "easeOut" } // Smoother exit tween
                    }}
                    // Mobile: Floating card style above BottomNav (h-16)
                    // Desktop: Edge-to-edge at the bottom
                    // Corrected className placement and value (bottom-16, z-50)
                    className="fixed bottom-18 left-4 right-4 z-30 flex h-16 items-center justify-between gap-4 rounded-lg border border-gray-200 bg-white px-4 shadow-lg dark:border-gray-700 dark:bg-gray-800 md:left-0 md:right-0 md:bottom-0 md:mx-0 md:rounded-none md:border-t md:border-l-0 md:border-r-0 md:border-b-0 md:shadow-md"
                    role="toolbar"
                    aria-label="Global Audio Player"
                >
                    {/* Left side: Play/Pause and Title */}
                    <div className="flex min-w-0 flex-1 items-center gap-3">
                <Button
                    variant="ghost"
                    size="small" // Assuming 'small' is a valid size for icon-like buttons
                    onClick={isPlaying ? pause : play}
                    disabled={isLoading || !!error}
                    aria-label={isPlaying ? 'Pause' : 'Play'}
                    className="flex-shrink-0"
                >
                    {isPlaying ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
                </Button>
                <div className="flex flex-col overflow-hidden">
                    <span className="truncate text-sm font-medium text-gray-800 dark:text-gray-100">
                        {isLoading ? 'Loading...' : error ? 'Error' : currentMedia?.title || 'Audio Guide'}
                    </span>
                    {error && <span className="truncate text-xs text-red-500">{error.message}</span>}
                </div>
            </div>

            {/* Center: Progress Bar and Time */}
            {/* Center: Progress Bar and Time */}
            <div className="hidden flex-1 items-center gap-2 md:flex">
                <span className="w-10 text-right text-xs text-gray-600 dark:text-gray-400">{formatTime(currentTime)}</span>
                 {/* Reverted to basic div progress bar */}
                 <div
                    className="w-full cursor-pointer"
                    onClick={handleProgressClick}
                    role="slider"
                    aria-valuemin={0}
                    aria-valuemax={duration}
                    aria-valuenow={currentTime}
                    aria-valuetext={`Audio progress: ${formatTime(currentTime)} of ${formatTime(duration)}`}
                    tabIndex={0} // Make it focusable
                    onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => { // Added type for event
                        if (e.key === 'ArrowLeft') seek(currentTime - 5);
                        if (e.key === 'ArrowRight') seek(currentTime + 5);
                    }}
                >
                    <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-600">
                        <div
                            className="h-full bg-blue-500 transition-all duration-150 ease-linear"
                            style={{ width: `${progressPercentage}%` }}
                        />
                    </div>
                </div>
                <span className="w-10 text-left text-xs text-gray-600 dark:text-gray-400">{formatTime(duration)}</span>
            </div>

            {/* Right side: Volume, Rate, Close */}
            <div className="flex items-center gap-2"> {/* Increased gap slightly */}
                 {/* Volume Control */}
                 <div className="relative" ref={volumeControlRef}>
                     <Button
                            variant="ghost"
                            size="small"
                            onClick={() => setShowVolumeSlider(!showVolumeSlider)}
                            aria-label={isMuted ? 'Unmute' : 'Adjust volume'}
                            aria-haspopup="true" // Indicate pop-up nature
                            aria-controls="volume-slider-popup" // Link to the pop-up
                            aria-expanded={showVolumeSlider} // Indicate if pop-up is open
                        >
                            {isMuted || volume === 0 ? (
                                <VolumeX size={18} />
                            ) : volume < 0.5 ? (
                                <Volume1 size={18} />
                            ) : (
                                <Volume2 size={18} />
                            )}
                        </Button>
                     {showVolumeSlider && (
                         <div
                             id="volume-slider-popup"
                             className="absolute bottom-12 right-0 z-10 rounded-lg border bg-white p-2 shadow-lg dark:border-gray-700 dark:bg-gray-800"
                             role="dialog" // Role for the pop-up
                             aria-label="Volume control"
                         >
                             <input
                                 autoFocus // Focus slider when popup opens
                                 type="range"
                                 min="0"
                                 max="1"
                                 step="0.01"
                                 value={isMuted ? 0 : volume}
                                 onChange={(e: React.ChangeEvent<HTMLInputElement>) => { // Added type
                                     const newVolume = parseFloat(e.target.value);
                                     setVolume(newVolume);
                                     if (isMuted && newVolume > 0) {
                                         toggleMute(); // Unmute if volume is increased from 0
                                     }
                                 }}
                                 className="h-2 w-24 cursor-pointer appearance-none rounded-lg bg-gray-200 accent-blue-500 dark:bg-gray-700"
                                 aria-label="Volume"
                             />
                         </div>
                     )}
                 </div>

                {/* Playback Rate Control */}
                 <div className="relative" ref={playbackRateRef}>
                     <Button
                            variant="ghost"
                            size="small"
                            onClick={() => setShowRateOptions(!showRateOptions)}
                            className="w-10 text-xs"
                            aria-label={`Playback speed: ${playbackRate}x`}
                            aria-haspopup="true"
                            aria-controls="rate-options-popup"
                            aria-expanded={showRateOptions}
                        >
                            {playbackRate}x
                        </Button>
                     {showRateOptions && (
                         <div
                             id="rate-options-popup"
                             className="absolute bottom-12 right-0 z-10 w-20 rounded-lg border bg-white p-1 shadow-lg dark:border-gray-700 dark:bg-gray-800"
                             role="dialog" // Role for the pop-up
                             aria-label="Playback speed options"
                         >
                             {[0.5, 0.75, 1.0, 1.25, 1.5, 2.0].map((rate, index) => ( // Added index
                                 <Button
                                     key={rate}
                                     autoFocus={index === 0} // Focus first button when popup opens
                                     variant={playbackRate === rate ? 'secondary' : 'ghost'}
                                     size="small"
                                     className="w-full justify-center text-xs"
                                     onClick={() => {
                                         setPlaybackRate(rate);
                                         setShowRateOptions(false);
                                     }}
                                     role="menuitemradio" // Role for selectable items
                                     aria-checked={playbackRate === rate} // Indicate selected state
                                 >
                                     {rate}x
                                 </Button>
                             ))}
                         </div>
                     )}
                 </div>

                 {/* Transcript Toggle Button */}
                 <Button
                    variant={isTranscriptVisible ? "secondary" : "ghost"} // Indicate active state
                    size="small"
                    onClick={toggleTranscriptVisibility}
                    aria-label={isTranscriptVisible ? "Hide transcript" : "Show transcript"}
                    aria-pressed={isTranscriptVisible} // Indicate toggle state
                 >
                    <MessageSquare size={18} />
                 </Button>

                {/* Close Button */}
                <Button
                    variant="ghost"
                    size="small"
                    onClick={unloadAudio}
                    aria-label="Close audio player"
                >
                    <X className="h-5 w-5" />
                        </Button>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};
